import { createApi } from '@reduxjs/toolkit/query/react';

import { _errorHandler, _requestResultHandler } from '../utilits';
import { axiosBaseQuery } from '../../utils/services/rqt-axios';
import { NEO_SERVERS_API_REDUCER_KEY } from '../consts';
import {
    INeoServersResponse,
    INeoUserAccessResponse,
    ISetNeoUserAccessRequest,
    ISetMyNeoAccessRequest,
} from './types';

// Served by the Data Lake API, not by NEO — the whole point is to learn which
// NEO servers exist before pointing the NEO client at one.
export const neoServersApi = createApi({
    reducerPath: NEO_SERVERS_API_REDUCER_KEY,
    baseQuery: axiosBaseQuery(),
    tagTypes: ['NeoServers', 'NeoUserAccess'],
    endpoints: (builder) => ({
        getNeoServers: builder.query<INeoServersResponse, null>({
            query: () => ({
                url: '/neo/servers',
                method: 'GET',
            }),
            providesTags: ['NeoServers'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                _errorHandler(dispatch, queryFulfilled);
            },
        }),

        /*
         * The signed-in user's own access, resolved server-side from the
         * authenticated Cognito username.
         *
         * Not keyed on the client's email: that value comes from the OAuth
         * userinfo response and can differ from the address on the user row, in
         * which case tokens are written under one identity and read back under
         * another — the app finds nothing and falls back to tokens the selected
         * NEO rejects.
         */
        getMyNeoAccess: builder.query<INeoUserAccessResponse, void>({
            query: () => ({
                url: '/neo/access',
                method: 'GET',
            }),
            providesTags: ['NeoUserAccess'],
        }),

        setMyNeoAccess: builder.mutation<INeoUserAccessResponse, ISetMyNeoAccessRequest>({
            query: ({ serverId, groups, teams }) => ({
                url: `/neo/access/${encodeURIComponent(serverId)}`,
                method: 'PUT',
                data: { groups, teams },
            }),
            invalidatesTags: ['NeoUserAccess'],
        }),

        // Email-keyed variants, for admins granting access to other users.
        getNeoUserAccess: builder.query<INeoUserAccessResponse, string>({
            query: (userEmail) => ({
                url: `/neo/access/${encodeURIComponent(userEmail)}`,
                method: 'GET',
            }),
            providesTags: ['NeoUserAccess'],
        }),

        setNeoUserAccess: builder.mutation<INeoUserAccessResponse, ISetNeoUserAccessRequest>({
            query: ({ userEmail, serverId, groups, teams }) => ({
                url: `/neo/access/${encodeURIComponent(userEmail)}/${encodeURIComponent(serverId)}`,
                method: 'PUT',
                data: { groups, teams },
            }),
            invalidatesTags: ['NeoUserAccess'],
            async onQueryStarted({ serverId }, { dispatch, queryFulfilled }) {
                _requestResultHandler(dispatch, queryFulfilled, `NEO access saved for server "${serverId}".`);
            },
        }),
    }),
});

export const {
    useGetNeoServersQuery,
    useGetMyNeoAccessQuery,
    useSetMyNeoAccessMutation,
    useGetNeoUserAccessQuery,
    useLazyGetNeoUserAccessQuery,
    useSetNeoUserAccessMutation,
} = neoServersApi;
